import { get } from "lodash";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import MobileHamburgerPage from "./mobile-hamburger-page";
import "./mobile-header.m.css";
import Navbar from "./nav-bar";
import MobileNavItem from "./MobileNavItem";
import CustomHeader from "../../custom-header";
import { SupportStrip } from "../../../../pages/subscribe-page/support-strip";

const MobileHeader = ({
  signinHandler,
  onAccountHandler,
  isHardcodedHeader,
  showPrimaryHeader = true,
  showSecondaryHeader = true,
  isSubscriber,
  showRedirectSupportStrip
}) => {
  const getState = useSelector((state) => state);

  const primaryMenus = get(getState, ["qt", "data", "navigationMenu", "primaryMenus"], []);
  const secondaryMenus = get(getState, ["qt", "data", "navigationMenu", "secondaryMenus"], []);
  const isHamburgerMenuOpen = get(getState, ["isHamburgerMenuOpen"], false);
  const currentPath = get(getState, ["qt", "currentPath"], null);
  const primaryMenusRef = useRef(null);
  const primaryMenusModeRef = useRef("flow");
  const primaryMenusPlaceholderRef = useRef(null);
  const headerStackRef = useRef(null);
  const headerStackModeRef = useRef("flow");
  const headerStackPlaceholderRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (isHamburgerMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isHamburgerMenuOpen]);

  useEffect(() => {
    if (isHardcodedHeader !== "" || isHamburgerMenuOpen) return undefined;

    let ticking = false;
    lastScrollY.current = window.scrollY;

    // Remove spacers inserted by the previous implementation during a hot reload.
    const primaryMenusElement = primaryMenusRef.current;
    const primaryPlaceholder = primaryMenusPlaceholderRef.current;
    if (primaryMenusElement && primaryMenusElement.parentNode) {
      Array.from(primaryMenusElement.parentNode.children).forEach((child) => {
        const isLegacyPlaceholder =
          child !== primaryMenusElement &&
          child !== primaryPlaceholder &&
          child.style.visibility === "hidden" &&
          child.style.pointerEvents === "none";
        if (isLegacyPlaceholder) child.remove();
      });
    }

    const resetToFlow = (menus) => {
      menus.style.position = "";
      menus.style.top = "";
      menus.style.left = "";
      menus.style.width = "";
      menus.style.zIndex = "";
      menus.style.transform = "";
      menus.style.transition = "";
    };

    const releaseToFlow = (menus) => {
      const placeholder = primaryMenusPlaceholderRef.current;
      if (placeholder) placeholder.style.height = "0px";
      resetToFlow(menus);
    };

    const resetHeaderStackToFlow = (stack) => {
      stack.style.position = "";
      stack.style.top = "";
      stack.style.left = "";
      stack.style.width = "";
      stack.style.zIndex = "";
      stack.style.transform = "";
      stack.style.transition = "";
    };

    const releaseHeaderStackToFlow = (stack) => {
      const placeholder = headerStackPlaceholderRef.current;
      if (placeholder && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
      headerStackPlaceholderRef.current = null;
      resetHeaderStackToFlow(stack);
    };

    const showCompleteHeader = (stack, menus) => {
      if (!stack) return;

      // The primary menu can have a spacer while it is independently fixed.
      // Clear that state before fixing the combined header, otherwise the spacer
      // renders as a gap between the navbar and the menu.
      if (menus) {
        primaryMenusModeRef.current = "flow";
        releaseToFlow(menus);
      }

      if (headerStackModeRef.current === "fixed") return;
      headerStackModeRef.current = "fixed";
      const placeholder = document.createElement("div");
      placeholder.style.cssText = `height:${stack.offsetHeight}px;visibility:hidden;pointer-events:none;`;
      stack.parentNode.insertBefore(placeholder, stack);
      headerStackPlaceholderRef.current = placeholder;

      stack.style.position = "fixed";
      stack.style.top = "0";
      stack.style.left = "0";
      stack.style.width = "100%";
      stack.style.zIndex = "9998";
      stack.style.transform = "translateY(-100%)";
      stack.style.transition = "none";

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (headerStackModeRef.current !== "fixed") return;
          stack.style.transition = "transform 0.4s ease";
          stack.style.transform = "translateY(0)";
        });
      });
    };

    // Same flow -> fixed-top pattern used by tpml-dh, applied to the only
    // mobile menu stack in this project.
    const applyFixedTop = (menus) => {
      if (!menus || primaryMenusModeRef.current === "fixed-top") return;

      const fromFlow = primaryMenusModeRef.current === "flow";
      primaryMenusModeRef.current = "fixed-top";

      if (fromFlow) {
        const placeholder = primaryMenusPlaceholderRef.current;
        if (placeholder) placeholder.style.height = `${menus.offsetHeight}px`;

        menus.style.position = "fixed";
        menus.style.top = "0";
        menus.style.left = "0";
        menus.style.width = "100%";
        menus.style.zIndex = "9998";
        menus.style.transform = "translateY(-100%)";
        menus.style.transition = "none";

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (primaryMenusModeRef.current !== "fixed-top") return;
            menus.style.transition = "transform 0.4s ease";
            menus.style.transform = "translateY(0)";
          });
        });
      } else {
        menus.style.transform = "translateY(0)";
      }
    };

    const applyFlow = (menus, instant = false) => {
      if (!menus) return;
      if (instant) {
        primaryMenusModeRef.current = "flow";
        releaseToFlow(menus);
        return;
      }

      if (primaryMenusModeRef.current === "flow") return;
      primaryMenusModeRef.current = "flow";

      menus.style.transition = "transform 0.4s ease";
      menus.style.transform = "translateY(-100%)";
      menus.addEventListener("transitionend", function onTransitionEnd(event) {
        if (event.propertyName !== "transform") return;
        menus.removeEventListener("transitionend", onTransitionEnd);
        if (primaryMenusModeRef.current === "flow") releaseToFlow(menus);
      });
    };

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        const currentScrollY = Math.max(0, window.scrollY);
        const scrollDifference = currentScrollY - lastScrollY.current;
        const menus = primaryMenusRef.current;
        const headerStack = headerStackRef.current;
        const isAtBottom = currentScrollY + window.innerHeight >= document.documentElement.scrollHeight - 20;

        if (isAtBottom) {
          showCompleteHeader(headerStack, menus);
          lastScrollY.current = currentScrollY;
        } else if (currentScrollY === 0) {
          if (headerStackModeRef.current === "fixed") {
            headerStackModeRef.current = "flow";
            releaseHeaderStackToFlow(headerStack);
          }
          applyFlow(menus, true);
          lastScrollY.current = currentScrollY;
        } else if (Math.abs(scrollDifference) >= 8) {
          if (headerStackModeRef.current === "fixed") {
            headerStackModeRef.current = "flow";
            releaseHeaderStackToFlow(headerStack);
          }
          if (scrollDifference < 0 && currentScrollY > 82) {
            applyFixedTop(menus);
          } else {
            applyFlow(menus, scrollDifference < 0);
          }
          lastScrollY.current = currentScrollY;
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      primaryMenusModeRef.current = "flow";
      if (primaryMenusRef.current) releaseToFlow(primaryMenusRef.current);
      headerStackModeRef.current = "flow";
      if (headerStackRef.current) releaseHeaderStackToFlow(headerStackRef.current);
    };
  }, [isHamburgerMenuOpen, isHardcodedHeader]);

  if (isHardcodedHeader !== "") {
    return (
      <CustomHeader
        isHardcodedHeader={isHardcodedHeader}
        signinHandler={signinHandler}
        onAccountHandler={onAccountHandler}
      />
    );
  }

  if (isHamburgerMenuOpen) {
    return (
      <div styleName="overlay">
        <MobileHamburgerPage
          signinHandler={() => signinHandler("ham_menu")}
          onAccountHandler={onAccountHandler}
          primaryMenus={primaryMenus}
          secondaryMenus={secondaryMenus}
          currentPath={currentPath}
          isSubscriber={isSubscriber}
        />
      </div>
    );
  }

  const displayMenus = primaryMenus.slice(0, 5);

  return (
    <>
      <div ref={headerStackRef} styleName="secondary-stack">
        <div styleName="fixed-header">
          <Navbar
            signinHandler={signinHandler}
            onAccountHandler={onAccountHandler}
            isSubscriber={isSubscriber}
            isVerificationPending={showRedirectSupportStrip}
          />
        </div>

        <div ref={primaryMenusPlaceholderRef} styleName="primary-menus-placeholder" aria-hidden="true" />
        <div ref={primaryMenusRef} styleName="primary-menus">
          {displayMenus.map((menu, idx) => (
            <MobileNavItem key={idx} menu={menu} currentPath={currentPath} allMenus={displayMenus} currentIndex={idx} />
          ))}
        </div>
      </div>

      {showRedirectSupportStrip && <SupportStrip showSecondaryHeader={showSecondaryHeader} />}
    </>
  );
};

export default MobileHeader;
